/**
 * TypeScript mirror of the `fim-*` Rolodex `data` contract.
 *
 * Source of truth: `power/fim-service/fim_service/models.py` (SEAM #1) and
 * `power/fim-service/fim_service/api/*.py` (SEAM #2). Keep these in sync.
 *
 * Forecast READS these `fim-*` entry `data` payloads directly from Rolodex
 * (via `useGetRolodexEntriesByLoadOptions`); it ACTS via `fim-service`
 * (via `useFimService`). fim-service owns instance identity/lifecycle writes
 * and llm-service writes the absorbed fill — Forecast never writes instances
 * or templates' fill state (it authors templates and triggers
 * instantiate/refill only).
 */

import type { FilledSlot, RepairItem } from './templateTypes'

// ---------------------------------------------------------------------------
// Rolodex entry type constants (the `fim-*` family)
// ---------------------------------------------------------------------------

export const FIM_ENTRY_SOURCE_LIST = 'fim-source-list'
export const FIM_ENTRY_SOURCE_RECORD = 'fim-source-record'
export const FIM_ENTRY_SUBJECT = 'fim-subject'
/** Promoted, validated asset inventory. Unlike `fim-subject`, this projection is
 * stable across template-fill lifecycle changes. */
export const FIM_ENTRY_ASSET_INSTANCE = 'fim-asset-instance'
export const FIM_ENTRY_CONSTRAINT = 'fim-constraint'
export const FIM_ENTRY_REVIEW = 'fim-review'
export const FIM_ENTRY_FUSION_RUN = 'fim-fusion-run'

// ---------------------------------------------------------------------------
// Enums (string unions mirroring the pydantic Enums / registries)
//
// Source of truth: `power/fim-service/fim_service/models.py`, the adapter registry
// (`adapters/base.py`), and `fusion/diagnose.py`. Keep these in sync by hand.
// ---------------------------------------------------------------------------

export type SourceName =
  | 'cmms'
  | 'doc_nav'
  | 'agentic'
  | 'system_documents'
  | 'historian'
  | 'manual'

// "asset" is the product umbrella, not a stored kind — equipment and instrument
// are peer kinds. The service maps legacy kind="asset" data forward on parse.
// Classification is independent from review/disposition state. Useful
// non-assets remain subjects; meaningless evidence is suppressed instead of
// being assigned a junk kind.
export type SubjectKind =
  | 'equipment'
  | 'system'
  | 'subsystem'
  | 'pipeline'
  | 'instrument'
  | 'doc_reference'
  // A durable human "noise" verdict applied at fusion time; the projection is
  // kept but inert.
  | 'rejected'
  | 'non_asset'

export type SubjectValidationStatus = 'unvalidated' | 'validated' | 'stale'

/**
 * Asset Instance fill lifecycle. The instance owns this state; the absorbed
 * `fill` payload supplies detail but does not determine inventory membership.
 * Also mirrored to the entry's `fill_status` tag (queryable dimension).
 */
export type AssetInstanceFillStatus =
  | 'missing_type'
  | 'no_template'
  | 'queued'
  | 'running'
  | 'needs_review'
  | 'complete'
  | 'failed'
  | 'stale'

export type AssetStatus =
  | 'exists'
  | 'exists_connected'
  | 'not_connected'
  | 'proxy'
  | 'spare'
  | 'retired'

export type AliasKind =
  | 'canonical_tag'
  | 'functional_location'
  | 'description'
  | 'contextual_name'
  | 'ocr_variant'

export type Precision = 'high' | 'low'

export type ReferenceType =
  | 'cmms_row'
  | 'ocr_block'
  | 'doc_page'
  | 'pi_tag'
  | 'agent'
  | 'photo'

export type ConfidenceBasis =
  | 'structured_record'
  | 'regex'
  | 'llm'
  | 'corroboration'

// The crosswalk is a regenerable projection — only `proposed` (engine-derived) and
// `suppressed` (durable human noise-gate) are meaningful on it. `confirmed`/`rejected`
// live on the durable `fim-constraint`, not here (see models.py CrosswalkStatus).
export type CrosswalkStatus = 'proposed' | 'suppressed'

export type Provenance = 'deterministic' | 'agent_cited' | 'human'

// How a fusion run solved identity. `pattern`: the site has a tag grammar, so canonical
// tags are the join keys (0 over-merges). `mathematical`: no grammar → kind-agnostic
// structural clustering (union on any shared alias) with over-merge flagging.
export type ClusteringMode = 'pattern' | 'mathematical'

// A run's agent-edge policy: `reuse` replays stored agent edges (0 LLM); `regenerate`
// re-runs the agent and rewrites them.
export type AgentEdgePolicy = 'reuse' | 'regenerate'

export type ConstraintKind = 'must_link' | 'cannot_link'

export type ReviewAction =
  | 'accept'
  | 'reject'
  | 'edit'
  | 'merge'
  | 'split'
  | 'suppress'
  // "This tag is a misread of X": writes the durable ocr_variant constraint.
  | 'mark_variant'

// The registered source adapters (`fim_service/adapters/base.py` registry). The
// source-list `POST /sources` action accepts any of these; `ADAPTER_OPTIONS` in
// `features/modelOperations/fimShared.ts` decides which are offered in the Sources form.
export type FimAdapter =
  | 'poc_csv'
  | 'cmms_csv'
  | 'docnav_unit'
  | 'docnav_bbox'
  | 'pnid_graph'
  | 'constructor'
  | 'census'
  | 'process_data'

// ---------------------------------------------------------------------------
// Shared sub-objects
// ---------------------------------------------------------------------------

export interface Reference {
  type: ReferenceType
  ref: string
  locator?: string | null
  reason?: string | null
  // Optional document-region enrichment. `boxes` holds "bbox:x0,y0,x1,y1"
  // page-coordinate strings (see utils/selectedRegionsParam.ts).
  document_id?: string | null
  page?: number | null
  boxes?: string[] | null
}

export interface ParsedTag {
  unit?: string | null
  system?: string | null
  isa?: string | null
  number?: string | null
}

/**
 * The alias identity fields without evidence. Summary-shaped subject reads
 * (`shape=summary`) carry exactly this; a full `Alias` is assignable to it.
 */
export interface AliasSummary {
  value: string
  norm: string
  kind: AliasKind
  precision: Precision
  /** Absent on summary rows; always present on a full subject read. */
  references?: Reference[]
}

export interface Alias extends AliasSummary {
  references: Reference[]
}

// ---------------------------------------------------------------------------
// Entry `data` payloads
// ---------------------------------------------------------------------------

export interface SourceListData {
  name: string
  adapter: string
  params: Record<string, unknown>
  created_at: string
  record_count: number
  updated_at?: string | null
}

export interface SourceRecordData {
  source_list_id: string
  source: SourceName
  aliases: Alias[]
  document_targets?: Array<{
    document_id: string
    drawing_number?: string | null
    revision_code?: string | null
    document_type?: string | null
  }>
  parsed_tag?: ParsedTag | null
  kind_guess?: SubjectKind | null
  type_guess?: string | null
  status?: AssetStatus | null
  subsystem_hint?: string | null
  confidence: number
  confidence_basis?: ConfidenceBasis | null
  norm_identity?: boolean
  verified?: boolean | null
}

export interface CandidateAssociation {
  // Untyped at fill-time in the contract; surface what we know for review.
  subject_uid?: string
  name?: string
  similarity?: number
  reason?: string
  [k: string]: unknown
}

export interface FimSubjectData {
  name: string
  kind: SubjectKind
  type?: string | null
  type_confidence?: number | null
  status?: AssetStatus | null
  subsystem_id?: string | null
  existence_confidence: number
  source_set: SourceName[]
  aliases: Alias[]
  constituent_source_record_ids: string[]
  candidate_associations: CandidateAssociation[]
  review_reason?: string | null
  /** Stable machine-readable companion to review_reason (queue triage):
   * kind_conflict | type_conflict | name_only | ambiguous_name | over_merge |
   * identity_collision. */
  review_category?: string | null
  dossier?: Record<string, unknown> | null
  diagnosis?: TailDiagnosis | null // why this subject is unplaced + the recommended next rung
  fusion_run_id?: string | null // the run that produced this projection (latest-run scoping)
  resolved_by_tier?: number | null // which escalation tier merged/resolved this subject (2|3|4), else null
  /** Content-addressed projection revision used for optimistic review writes. */
  revision?: string | null
  /** Source-vote fingerprint used to explain material evidence changes. */
  source_evidence_revision?: string | null
  /** Explicit validation lifecycle; absent on legacy projections. */
  validation_status?: SubjectValidationStatus | null
  /** Revision that was accepted by a reviewer, when still available. */
  validated_revision?: string | null
}

/**
 * The fill result ABSORBED into an Asset Instance (there is no separate fill
 * entry). `slots` are llm-service `FilledSlot` payloads, verbatim.
 */
/**
 * A fill as stored. `GET /instances?shape=summary` returns the SAME shape
 * minus `slots`/`repair_items` — the bulk of it — plus the slot tally the
 * list cell needs, so a catalog load never carries a site's slot detail.
 * Anything reading `slots` must ask for one instance at full shape.
 */
export interface FimInstanceListOptions {
  shape?: 'full' | 'summary'
  /** Skip the staleness pass — it belongs off a paint's critical path. */
  reconcile?: boolean
}

export interface FimAssetInstanceFill {
  /** llm-service `FilledTemplate.status`. */
  status: 'complete' | 'incomplete_required' | 'incomplete_optional'
  /** "filled/required" over REQUIRED slots, per family (e.g. "5/6"). */
  pi_tag_coverage: string
  document_coverage: string
  required_unmet: string[]
  slots?: FilledSlot[]
  repair_items?: RepairItem[]
  filled_at: string
  /** Hash of (subject_revision, template_version, filled_at). */
  fill_revision: string
  /** Summary shape only — the tally standing in for `slots`. */
  slots_total?: number
  slots_filled?: number
  slots_gaps?: number
}

/**
 * Durable instantiated asset keyed to a validated Subject: identity +
 * lifecycle plus the absorbed fill result. Entry id is
 * `fim-asset-instance-<subject_uid>`; fim-service writes identity/lifecycle,
 * llm-service writes `fill` and the terminal `fill_status`.
 */
export interface FimAssetInstanceData {
  subject_uid: string
  /** Subject projection revision at instantiate/fill time. */
  subject_revision: string
  canonical_name: string
  kind: 'equipment' | 'instrument'
  /** Subject's taxonomy type at instantiate time. */
  type?: string | null
  aliases?: string[]
  subsystem_id?: string | null
  template_id?: string | null
  template_version?: string | null
  fill_status: AssetInstanceFillStatus
  fill_error?: string | null
  /** Stamped by llm-service when a fill starts (with `fill_status=running`);
   * never cleared, only overwritten by the next fill start. A `running`
   * instance whose stamp is older than 45 minutes is abandoned — services
   * treat it as not-running (fill-ownership protocol v2 §1-2). */
  fill_started_at?: string | null
  /** Null until the first fill completes; retained on `stale` instances. */
  fill?: FimAssetInstanceFill | null
}

/** Deterministic entry id: `fim-asset-instance-<subject_uid>`. */
export const assetInstanceEntryId = (subjectUid: string): string =>
  `${FIM_ENTRY_ASSET_INSTANCE}-${subjectUid}`

/** Deterministic entry id: `fim-subject-<subject_uid>`. */
export const subjectEntryId = (subjectUid: string): string =>
  `${FIM_ENTRY_SUBJECT}-${subjectUid}`

/** Tail-diagnosis verdicts (rungs 0/1) — mirrors `fusion/diagnose.py`, ordered
 * most-actionable → least. `vendor_code` = an opaque vendor/KKS code on a drawing. */
export type DiagnosisVerdict =
  | 'ocr_variant'
  | 'parse_artifact'
  | 'near_miss'
  | 'name_split'
  | 'coverage_gap'
  | 'genuine_single_source'
  | 'vendor_code'
  | 'needs_document'
  | 'needs_human'

export interface TailDiagnosis {
  verdict: DiagnosisVerdict
  confidence: number
  evidence: string[]
  recommended_action: string
  rung: number
  auto_apply: boolean
}

/**
 * One alias row from the fim-service aliases endpoint (the crosswalk
 * successor). Rows are scoped to a fusion run; `is_identity` marks the
 * routing keys that resolve an alias norm to exactly one subject.
 */
export interface FimAliasRow {
  subject_uid: string
  norm: string
  value: string
  kind: AliasKind
  precision: Precision
  is_identity: boolean
  provenance: Provenance
  first_seen_run?: string | null
  status: CrosswalkStatus
}

export interface ConstraintData {
  alias_a_norm: string
  alias_b_norm: string
  kind: ConstraintKind
  source_review_id: string
  created_at: string
}

/**
 * A durable agent-proposed alias↔alias merge edge. Stored as a `fim-constraint`
 * entry tagged `provenance=agent`, so it loads via the same query as human
 * constraints and is partitioned by that tag. Provisional until a human ratifies
 * it into a (human) constraint.
 */
export interface AgentEdgeData {
  alias_a_norm: string
  alias_b_norm: string
  confidence: number
  rationale?: string | null
  proposed_run: string
  last_seen_run?: string | null
  created_at: string
}

export interface LegacyReviewData {
  action: ReviewAction
  alias_keys: string[]
  target_subject_uid?: string | null
  expected_revision?: string | null
  canonical_name?: string | null
  kind?: SubjectKind | null
  source_record_ids?: string[]
  exists?: boolean | null
  type?: string | null
  reason?: string | null
  review_kind?: string
  template_id?: string | null
  slot_key?: string | null
  to_ref?: string | null
  to_subject_uid?: string | null
  // mark_variant payload (corrected_norm is the server-normalized corrected_tag).
  variant_norm?: string | null
  corrected_tag?: string | null
  corrected_norm?: string | null
  evidence_snapshot: Record<string, unknown>
  reviewer: string
  ts: string
}

export interface StructuralReviewData {
  review_kind: 'subject_decision'
  operation:
    | 'merge_subjects'
    | 'split_subject'
    | 'correct_ocr'
    | 'revert_decision'
  decision_id: string
  affected_source_record_ids: string[]
  target_subject_uids: string[]
  actual_result_subject_ids?: string[]
  reviewer: string
  reason?: string | null
  created_at: string
  /** Explicitly absent legacy fields keep mixed-ledger reads safe. */
  action?: never
  alias_keys?: never
  ts?: never
}

export type ReviewData = LegacyReviewData | StructuralReviewData

export const isStructuralReviewData = (
  data: ReviewData
): data is StructuralReviewData => data.review_kind === 'subject_decision'

// Which escalation tiers a fusion run should execute. Tiers 0 (deterministic
// self-checks) and 1 (OCR/permutation resolver) are always on (free, in-process)
// and are NOT represented here. These are the billable LLM tiers (there is
// deliberately no Tier 2 — the backend has none, so no toggle for it either).
export interface TierConfig {
  adjudicate: boolean // Tier 3 — per-subject LLM adjudication over blocking candidates
  read_tag: boolean // Tier 4 — per-subject scoped vision read of the drawn tag
}

// One row of the tier-run report: what a tier did this run.
export interface TierRun {
  tier: number // 3 | 4
  name: string // 'adjudicate' | 'read_tag'
  ran: boolean
  processed: number // items the tier considered
  resolved: number // items it successfully bridged/corrected
  produced: number // durable edges/corrections it wrote
  calls: number // LLM calls spent
}

/** A namespace rule — position in `Conventions.namespaces` is the leader priority. */
export interface NamespaceRule {
  name: string
  pattern: string
}

/** Per-run tag conventions carried INLINE in the run config (mirrors models.py
 * `Conventions`) — applied at run time (normalize + parse + namespace), so a
 * convention change takes effect on the next run without re-ingesting sources. */
export interface Conventions {
  uppercase: boolean
  transforms: { pattern: string; replace: string }[]
  parse: string[]
  namespaces: NamespaceRule[]
  isa_equivalence: Record<string, string[]>
  default_unit?: string | null
}

export interface FusionRunData {
  source_list_ids: string[]
  started: string
  ended?: string | null
  status: string // running | done | error
  // counts holds numbers (subjects, confident, …), string-valued run descriptors
  // (`mode`, `agent_edges`), and arbitrarily nested count groups (`ref_hygiene`,
  // matching link-tier maps, …); render via `flattenRunCounts` so an object
  // value never reaches a React child.
  counts: Record<string, number | string | Record<string, unknown>>
  mode?: ClusteringMode // how identity was solved (pattern vs mathematical)
  agent_edges?: AgentEdgePolicy // reuse | regenerate
  tier_runs?: TierRun[] // per-tier report for this run (tiers 3-4)
  tiers?: TierConfig | null
  conventions?: Conventions | null
  phase?: string | null
  error?: string | null
}

/** One newly-created subject↔datapoint link sampled onto a matching run. */
export interface MatchRunLink {
  subject_uid: string
  subject_name?: string | null
  datapoint_id: string
  pi_tag: string
  tier: string
}

/** One unmatched datapoint sampled into a diagnostics category. */
export interface MatchRunDiagnosticExample {
  name: string
  description: string
  detail: string
}

/**
 * One reason-bucket of a run's unmatched datapoints. `label` is the backend's
 * plain-English name for the bucket and is shown verbatim.
 */
export interface MatchRunDiagnosticCategory {
  key: string
  label: string
  count: number
  examples: MatchRunDiagnosticExample[]
}

/** One datapoint whose key resolved to several competing subjects. */
export interface MatchRunAmbiguity {
  datapoint: string
  key: string
  /** Competing subject entry ids. */
  subjects: string[]
  /** Display names positionally matching `subjects`; absent on older runs. */
  subject_names?: string[]
}

/** One unmatched datapoint from the full-list endpoint. */
export interface MatchRunUnmatchedDatapoint {
  name: string
  description: string
  detail: string
  category: string
  family: string
}

/**
 * The run's complete unmatched/ambiguous lists, from fim-service
 * `GET /sites/{site}/matching/runs/{run_id}/diagnostics/full`. 404 means the
 * run predates full-list recording.
 */
export interface MatchRunFullDiagnostics {
  unmatched: MatchRunUnmatchedDatapoint[]
  ambiguous: MatchRunAmbiguity[]
}

/**
 * `data.diagnostics` of a match-run entry: the unmatched breakdown. Absent on
 * runs recorded before fim-service started writing it.
 */
export interface MatchRunDiagnostics {
  categories: MatchRunDiagnosticCategory[]
  ambiguous: MatchRunAmbiguity[]
  /** True total behind the sampled `ambiguous` list; absent on older runs. */
  ambiguous_total?: number
  /** Unmatched-datapoint counts by tag family. */
  families: Record<string, number>
}

/**
 * Stats of the opt-in LLM adjudication pass over near-miss candidates.
 * Accepted links land as tier `llm_binding` in the regular counts/new_links.
 */
export interface MatchRunLlmStats {
  requested: boolean
  groups: number
  calls: number
  proposed: number
  accepted: number
  rejected: number
  unsure: number
  cache_hits: number
}

/** `DatapointMatchingStats.as_counts()` — the matcher's verbatim report. */
export interface MatchRunCounts {
  datapoints?: number
  already_linked?: number
  no_instrument_expected?: number
  linked?: Record<string, number>
  subjects_linked?: number
  ambiguous?: number
  unmatched?: number
}

/**
 * One standalone time-series-linkage run, from fim-service
 * `GET /api/v1/sites/{site}/matching/runs`. Carries the matcher stats plus the
 * comparability fields (`service_version`, zero-pad capability) so iterations
 * of the matcher can be measured against each other.
 */
export interface MatchRunView {
  run_id: string
  site: string
  status: string // running | done | error
  started?: string | null
  ended?: string | null
  error?: string | null
  fusion_run_id?: string | null
  counts: MatchRunCounts
  subjects_patched: number
  new_links: MatchRunLink[]
  service_version?: string | null
  number_zero_pad_equivalence?: boolean
  diagnostics?: MatchRunDiagnostics | null
  llm?: MatchRunLlmStats | null
}

/** Kickoff ack for `POST /matching/run` — poll the runs list for progress. */
export interface MatchRunSummary {
  run_id: string
  status: string
}

/**
 * The layered normalization/clustering config for a site (served read-only by
 * fim-service `GET /api/v1/config/{site}`). `has_tag_grammar=false` ⇒ mathematical mode.
 */
export interface SiteConfig {
  site: string
  mode: ClusteringMode
  has_tag_grammar: boolean
  parse: string[] // tag-grammar regex patterns
  transforms: { pattern: string; replace: string }[]
  default_unit?: string | null
  isa_equivalence: Record<string, string[]>
  uppercase: boolean
}

// ---------------------------------------------------------------------------
// Loaded-entry envelope helper
// ---------------------------------------------------------------------------

/**
 * A `fim-*` entry as loaded from Rolodex. The `/api/v2/entries/load` endpoint
 * returns `{ entry, links? }`; the unwrapping helpers in `hooks.ts` flatten to
 * this shape so callers get `{ id, type, tags, data }` directly.
 */
export interface FimEntry<TData = Record<string, unknown>> {
  id: string
  type: string
  tags: Record<string, string>
  data: TData
}

export type FimSourceListEntry = FimEntry<SourceListData>
export type FimSourceRecordEntry = FimEntry<SourceRecordData>
export type FimAssetInstanceEntry = FimEntry<FimAssetInstanceData>
export type FimFusionRunEntry = FimEntry<FusionRunData>
export type FimConstraintEntry = FimEntry<ConstraintData>
export type FimAgentEdgeEntry = FimEntry<AgentEdgeData>

/**
 * The slim `data` payload of a summary-shaped subject read: identity, review,
 * and catalog-filter fields only — NO references/dossier/diagnosis/
 * page_targets/candidate_associations/constituent_source_record_ids. A full
 * `FimSubjectData` is assignable to it, so full-subject consumers compose
 * with summary-typed helpers; the reverse direction is a compile error, which
 * is what forces detail fetches where evidence fields are read.
 */
export interface FimSubjectSummaryData {
  name: string
  kind: SubjectKind
  type?: string | null
  subsystem_id?: string | null
  existence_confidence: number
  source_set: SourceName[]
  review_reason?: string | null
  review_category?: string | null
  revision?: string | null
  validation_status?: SubjectValidationStatus | null
  aliases: AliasSummary[]
}

/** Why a search returned a row: which field matched and the visible text.
 * For `alias_norm` hits `text` is the alias VALUE whose norm matched, so the
 * searched term may not appear verbatim in it. */
export interface FimSubjectMatch {
  source: 'name' | 'type' | 'alias_value' | 'alias_norm'
  text: string
  alias_kind?: string | null
}

/**
 * A Subject as served by the fim-service subjects endpoints (Postgres-backed
 * projection). `id` keeps the `fim-subject-<uid>` form because it is the join
 * key against instances, reviews, and viewer asset ids; `status` and
 * `datapoints` are top-level response fields, not entry tags.
 */
interface FimSubjectRecordBase {
  id: string
  subjectUid: string
  status: 'review' | 'proposed'
  datapoints: string[]
  /** Present on search responses only. */
  match?: FimSubjectMatch
}

/** A summary-shaped Subject (`shape=summary` list/by-ids reads). */
export interface FimSubjectSummary extends FimSubjectRecordBase {
  data: FimSubjectSummaryData
}

/** A full Subject (detail / by-ids reads without the summary shape). */
export interface FimSubjectRecord extends FimSubjectRecordBase {
  data: FimSubjectData
}

export type FimSubjectEntry = FimSubjectRecord

/** One datapoint's resolved subject links from `/subjects/by-datapoints`. */
export interface FimDatapointSubjectLink {
  datapoint_id: string
  subject_uids: string[]
}
